import React, { forwardRef, useState, useEffect } from 'react';

import type { FilterPopoverProps } from '@vlabs/monokit/shared/ui/popovers/filter-popover';
import { FilterPopover } from '@vlabs/monokit/shared/ui/popovers/filter-popover';
import { ButtonSwitch, ButtonSwitchGroup } from '@vlabs/uikit';

import { useSearchPageTranslation } from '../../../../i18n';
import st from '../popovers.module.sass';
import { Tabs } from './tabs';

type TTabs = 'map' | 'address' | 'coordinates';

const geolocationFields = [
  'cities', 'areas', 'districts', 'streets', 'house_numbers',
  'origin_longitude', 'longitude_delta', 'origin_latitude', 'latitude_delta',
];

export interface LocationFilterPopoverProps extends FilterPopoverProps<string> {
  includedAttributes: string[];
}

function LocationFilterPopover(
  {
    includedAttributes,
    ...props
  }: LocationFilterPopoverProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const [activeTab, setActiveTab] = useState<TTabs>('address');

  const { t } = useSearchPageTranslation();

  const isMapEnabled = includedAttributes.includes('map');

  useEffect(() => {
    if (isMapEnabled) {
      setActiveTab('map');
      return;
    }
    setActiveTab('address');
  }, [isMapEnabled]);

  return (
    <FilterPopover
      {...props}
      className={st.LocationFilterPopover}
      name={geolocationFields}
      ref={ref}
    >
      <ButtonSwitchGroup
        data-testid="geolocationFiltersSwitch"
        onChange={setActiveTab}
        size="small"
        value={activeTab}
        variant="square"
      >
        {!!isMapEnabled && (
          <ButtonSwitch
            value="map"
          >
            {t('filters.event_filters.location.tabs.map')}
          </ButtonSwitch>
        )}

        <ButtonSwitch
          value="address"
        >
          {t('filters.event_filters.location.tabs.address')}
        </ButtonSwitch>
        <ButtonSwitch
          value="coordinates"
        >
          {t('filters.event_filters.location.tabs.coordinates')}
        </ButtonSwitch>
      </ButtonSwitchGroup>
      {activeTab === 'map' && <Tabs.MapTab />}
      {activeTab === 'coordinates' && <Tabs.CoordinatesTab />}
      {activeTab === 'address' && <Tabs.AddressTab />}
    </FilterPopover>
  );
}

const LocationFilterPopoverWithRef = forwardRef(LocationFilterPopover);
export { LocationFilterPopoverWithRef as LocationFilterPopover };
